/*
 * @description
 * Definisce un contesto di navigazione globale utilizzando React Context API e React Router.
 *
 * Il `NavigationContext` permette ai componenti dell'applicazione di accedere e gestire la navigazione
 * in modo centralizzato, fornendo:
 * - Il percorso attuale (`currentRoute`).
 * - I parametri di navigazione (`params`).
 * - Una funzione `onNavigate` per cambiare pagina e passare parametri.
 *
 * Il `NavigationProvider` utilizza `useNavigate` e `useLocation` di React Router per gestire
 * gli spostamenti tra le pagine e mantenere lo stato dei parametri di navigazione.
 *
 * Questo approccio facilita il controllo della navigazione in tutta l'app senza dover passare manualmente
 * la funzione di `navigate` ai componenti figli.
 */
import { createContext, useState, ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { logger } from "../utils/logger";

// Definisco l'interfaccia del contesto
interface NavigationContextProps {
    currentRoute: string;
    params: Record<string, any>;
    onNavigate: (route: string, params?: Record<string, any>) => void;
}

// Valori di default (non usati se il Provider è presente)
const defaultNavigation: NavigationContextProps = {
    currentRoute: "/",
    params: {},
    onNavigate: () => {
        logger.warn("Navigazione non è stata implementata");
    }
};

// Creo il contesto
const NavigationContext = createContext<NavigationContextProps>(defaultNavigation);

interface NavigationProviderProps {
    children: ReactNode;
}

// Provider che integra React Router
export const NavigationProvider = ({ children }: NavigationProviderProps) => {
    const navigate = useNavigate();
    const location = useLocation(); // Ottiene la posizione attuale
    const [params, setParams] = useState<Record<string, any>>({});

    useEffect(() => {
        if (location.state) {
            setParams(location.state);
        }
    }, [location.state]);

    const onNavigate = (route: string, newParams: Record<string, any> = {}) => {
        if (location.pathname !== route) {
            //evita navigazione se sei già sulla stessa pagina
            setParams(newParams);
            navigate(route, { state: newParams }); // Usa React Router per navigare
        }
    };

    return (
        <NavigationContext.Provider value={{ currentRoute: location.pathname, params, onNavigate }}>
            {children}
        </NavigationContext.Provider>
    );
};

export default NavigationContext;
